import { useMutation, useQuery } from "@tanstack/react-query";
import useAxiosInstance from "./axiosInstance.utils";
import { errorFormat } from "./handleError.utils";
import { onSuccessMessage, queryClient } from "./reactQuery.utils";
import { arrayMap } from "./array.utils";

/**
 * :::::::MUTATIONS:::::::::::
 */

export function useCreatePostOfficeMutation(keyData) {
  const { postApi } = useAxiosInstance();
  async function createPostOfficeApi(data) {
    try {
      const api = `/post-office`;
      const apiResponse = await postApi(api, data);
      return apiResponse;
    } catch (error) {
      return errorFormat(error);
    }
  }

  const { isPending: loading, mutateAsync } = useMutation({
    mutationKey: ["createPostOfficeApi", ...keyData],
    mutationFn: (data) => createPostOfficeApi(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries("post-offices");
      onSuccessMessage(data, 201);
    },
    onError: (err) => {
      return errorFormat(err);
    }
  });

  return { loading, createPostOffice: mutateAsync };
}

export function useUpdatePostOfficeMutation(keyData) {
  const { putApi } = useAxiosInstance();
  async function updatePostOfficeApi(data) {
    try {
      const api = `/post-office`;
      const apiResponse = await putApi(api, data);
      return apiResponse;
    } catch (error) {
      return errorFormat(error);
    }
  }
  const { isPending: loading, mutateAsync } = useMutation({
    mutationKey: ["updatePostOfficeApi", ...keyData],
    mutationFn: (data) => updatePostOfficeApi(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries("post-offices");
      onSuccessMessage(data, 200);
    },
    onError: (err) => {
      return errorFormat(err);
    }
  });

  return { loading, updatePostOffice: mutateAsync };
}

export function useDeletePostOfficeMutation(keyData) {
  const { deleteApi } = useAxiosInstance();
  async function deletePostOfficeApi(id) {
    try {
      const api = `/post-office/${id}`;
      const apiResponse = await deleteApi(api);
      return apiResponse;
    } catch (error) {
      return errorFormat(error);
    }
  }

  const { isPending: loading, mutateAsync } = useMutation({
    mutationKey: ["deletePostOfficeApi", ...keyData],
    mutationFn: (data) => deletePostOfficeApi(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries("post-offices");
      onSuccessMessage(data, 200);
    },
    onError: (err) => {
      return errorFormat(err);
    }
  });

  return { loading, deletePostOffice: mutateAsync };
}

/**
 * :::::::QUERIES:::::::::::
 */
export function useGetAllPostOfficeQuery({ query, options = {}, key = "" }) {
  const { getApi } = useAxiosInstance();
  async function getAllPostOfficeApi(query) {
    try {
      const api = `/post-offices${query ?? ""}`;
      const apiResponse = await getApi(api);
      return apiResponse;
    } catch (error) {
      return errorFormat(error);
    }
  }

  const {
    data,
    isPending: loading,
    ...rest
  } = useQuery({
    queryKey: [`getAllPostOffice-${query ?? ""}${key ? "-" + key : ""}`],
    queryFn: () => getAllPostOfficeApi(query),
    ...options
  });
  const newData = data?.data?.data ?? {};
  const parents = data?.data?.parents ?? [];
  return { data: newData, parents, loading, ...rest };
}

export function postOfficeMakeNameWithPostCode(data) {
  const name_post_code = `${data?.name ?? ""} ${data?.post_code ? `- ${data?.post_code}` : ""}`;
  return {
    name_post_code,
    ...data
  };
}

export function postOfficeParseNamePostCode(data) {
  return arrayMap(data, (item) => postOfficeMakeNameWithPostCode(item)) || [];
}
